import React, { memo, useEffect, useState } from "react";
import {
  Stack,
  Grid,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { motion } from "framer-motion";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { useDispatch } from "react-redux";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";

//FUNCTION
const RejectionReasonsCard = ({
  isChanged,
  setIsChanged,
  editData,
  setEditData,
}) => {
  //#region code
  const dispatch = useDispatch();

  const formInitial = {
    reasons: "",
    status: 1,
  };
  const [formData, setFormData] = useState(formInitial);
  const [isEdit, setIsEdit] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked ? 1 : 0,
    }));
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setFormData({
        id: editData.id,
        reasons: editData.reasons || "",
        status: editData.status === undefined ? 1 : Number(editData.status),
      });
      setIsEdit(true);
    }
  }, [editData]);

  const clear = () => {
    setFormData(formInitial);
    setValidationErrors({});
    setIsEdit(false);
    setEditData({});
  };

  const handleFormValidation = () => {
    const errors = {};
    if (!formData.reasons || !formData.reasons.trim()) {
      errors.reasons = "required";
    } else if (formData.reasons.length > 255) {
      errors.reasons = "Max 255 characters";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const applyServerErrors = (res) => {
    if (res?.errors && typeof res.errors === "object") {
      const mapped = {};
      Object.keys(res.errors).forEach((key) => {
        mapped[key] = Array.isArray(res.errors[key])
          ? res.errors[key][0]
          : res.errors[key];
      });
      setValidationErrors((prev) => ({ ...prev, ...mapped }));
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      dispatch(triggerLoader());

      const valid = handleFormValidation();
      if (valid === false) {
        return false;
      }

      const dataToSend = {
        ...formData,
        status: Number(formData.status) === 1 ? 1 : 0,
      };

      if (!isEdit) {
        const res = await configServ.createRejectionReason(dataToSend);
        if (res.status === 201 || res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          handleOpenDialog(
            "Success",
            res.message || "Rejection reason created successfully.",
            "success"
          );
        } else if (res.status === 422) {
          applyServerErrors(res);
          handleOpenDialog(
            "Warning",
            res.message || "Validation failed.",
            "warning"
          );
        } else {
          handleOpenDialog(
            "Error",
            res.message || "Failed to create rejection reason.",
            "error"
          );
        }
      } else {
        const res = await configServ.updateRejectionReason(dataToSend);
        if (res.status === 200) {
          setIsChanged(!isChanged);
          clear();
          handleOpenDialog(
            "Success",
            res.message || "Rejection reason updated successfully.",
            "success"
          );
        } else if (res.status === 422) {
          applyServerErrors(res);
          handleOpenDialog(
            "Warning",
            res.message || "Validation failed.",
            "warning"
          );
        } else if (res.status === 404) {
          handleOpenDialog(
            "Error",
            res.message || "Rejection reason not found.",
            "error"
          );
        } else {
          handleOpenDialog(
            "Error",
            res.message || "Failed to update rejection reason.",
            "error"
          );
        }
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };
  //#endregion

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <motion.div
        style={{
          width: "100%",
          backgroundColor: "#e3f3fc",
          padding: 20,
          borderRadius: "10px",
          border: "none",
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.1, delay: 0.1 }}
      >
        <Grid container alignItems="flex-end" justifyContent="flex-start" spacing={1}>
          <Grid item xs={12} md={12} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "16px", fontWeight: "bolder" }}>
              Rejection Reason Detail
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              id="reasons"
              name="reasons"
              label="Reason*"
              variant="outlined"
              fullWidth
              size="small"
              inputProps={{ maxLength: 255 }}
              value={formData.reasons || ""}
              onChange={handleChange}
              error={!!validationErrors.reasons}
              helperText={
                typeof validationErrors.reasons === "string" &&
                validationErrors.reasons !== "required"
                  ? validationErrors.reasons
                  : ""
              }
              sx={{ backgroundColor: "white" }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormGroup sx={{ display: "flex", flexDirection: "row" }}>
              <FormControlLabel
                name="status"
                control={
                  <Checkbox checked={Number(formData.status) === 1} />
                }
                onChange={handleChangeCheckbox}
                label="Active"
              />
            </FormGroup>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="space-between" px={2}>
          <Grid
            item
            container
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            mt={1}
          >
            <Stack direction={"row"} spacing={2}>
              <Button
                onClick={clear}
                variant="contained"
                color="secondary"
                startIcon={<RotateLeftIcon />}
              >
                Reset
              </Button>
              <Button
                onClick={handleSubmit}
                autoFocus
                variant="contained"
                color={isEdit ? "success" : "primary"}
                startIcon={isEdit ? <UpdateIcon /> : <SaveIcon />}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </motion.div>
    </>
  );
};

export default memo(RejectionReasonsCard);
